import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { 
  Download, 
  Filter,
  FileSpreadsheet,
  Check,
  X,
  Users,
  ChevronDown,
  ChevronUp,
  Printer,
  AlertCircle,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const filterTypes = [
  { value: "all", label: "All Staff" },
  { value: "division", label: "By Division" },
  { value: "pool", label: "Pool" },
  { value: "skippers", label: "Skippers" },
  { value: "mariner", label: "Mariner" },
  { value: "navigator", label: "Navigator" },
  { value: "boating", label: "Boating" },
  { value: "health", label: "Health Center" },
  { value: "supervisor", label: "Supervisors" },
  { value: "transportation", label: "Transportation" },
  { value: "office", label: "Office" },
];

function BooleanCell({ value }) {
  return value ? (
    <div className="flex justify-center">
      <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center">
        <Check className="h-4 w-4 text-emerald-600" strokeWidth={2.5} />
      </div>
    </div>
  ) : (
    <div className="flex justify-center">
      <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center">
        <X className="h-4 w-4 text-slate-400" strokeWidth={2.5} />
      </div>
    </div>
  );
}

function YesNoCell({ value }) {
  return (
    <div className="flex justify-center">
      <span
        className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${
          value
            ? "bg-emerald-50 text-emerald-700 border-emerald-300"
            : "bg-red-50 text-red-700 border-red-300"
        }`}
      >
        {value ? "Yes" : "No"}
      </span>
    </div>
  );
}

// Status of a cert relative to camp season (date-based, never green for "has cert")
function CertStatusCell({ hasCert, expirationDate, campStart, campEnd }) {
  // No cert on file → gray X
  if (!hasCert) {
    return (
      <div className="flex justify-center">
        <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center" data-testid="cert-status-none">
          <X className="h-4 w-4 text-slate-400" strokeWidth={2.5} />
        </div>
      </div>
    );
  }

  // Has cert but no expiration date — amber warning (admin needs to add a date)
  if (!expirationDate) {
    return (
      <div className="flex justify-center" title="Cert on file but expiration date missing">
        <div className="h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center" data-testid="cert-status-missing-date">
          <AlertTriangle className="h-4 w-4 text-amber-600" strokeWidth={2.5} />
        </div>
      </div>
    );
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expDate = new Date(expirationDate);
  expDate.setHours(0, 0, 0, 0);

  // Expired
  if (expDate < today) {
    return (
      <div className="flex justify-center">
        <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center" data-testid="cert-status-expired">
          <AlertCircle className="h-4 w-4 text-red-600" strokeWidth={2.5} />
        </div>
      </div>
    );
  }

  // Expires on or before camp ends → amber (needs renewal before camp ends)
  if (campStart && campEnd) {
    const campEndDate = new Date(campEnd);
    campEndDate.setHours(0, 0, 0, 0);
    if (expDate <= campEndDate) {
      return (
        <div className="flex justify-center" title="Cert expires before or during camp — needs renewal">
          <div className="h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center" data-testid="cert-status-expires-during">
            <AlertTriangle className="h-4 w-4 text-amber-600" strokeWidth={2.5} />
          </div>
        </div>
      );
    }
  }

  // Covered through end of camp — GREEN check (fully valid for the season)
  return (
    <div className="flex justify-center">
      <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center" data-testid="cert-status-covered">
        <Check className="h-4 w-4 text-emerald-600" strokeWidth={2.5} />
      </div>
    </div>
  );
}

function DateCell({ value, hasCert, campStart, campEnd }) {
  if (!value) {
    if (hasCert) {
      return <span className="text-amber-600 italic text-xs" data-testid="date-missing">Date Missing</span>;
    }
    return <span className="text-slate-400">-</span>;
  }

  const date = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  let colorClass = "text-slate-900";
  if (date < today) {
    colorClass = "text-red-600 font-semibold";
  } else if (campEnd) {
    const campEndDate = new Date(campEnd);
    campEndDate.setHours(0, 0, 0, 0);
    if (date <= campEndDate) {
      colorClass = "text-amber-600 font-semibold";
    }
  }

  return (
    <span className={`${colorClass} text-xs`}>
      {date.toLocaleDateString()}
    </span>
  );
}

function CPRClassPanel({ token }) {
  const [classType, setClassType] = useState("cpr_first_aid");
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchClassData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/cpr-classes?class_type=${classType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClassData(response.data);
    } catch {
      toast.error("Failed to load CPR class data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClassData();
  }, [classType, token]);

  const handleExport = async () => {
    try {
      const response = await axios.get(
        `${API}/reports/cpr-classes/export?class_type=${classType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { csv_data, filename } = response.data;
      const blob = new Blob([csv_data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("CPR class report exported");
    } catch {
      toast.error("Failed to export");
    }
  };

  const toggleDate = (dateKey) => {
    setExpanded(prev => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="cpr-class-panel">
      <div className="p-4 border-b border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">CPR Class Roster</h2>
            <p className="text-sm text-slate-500">
              Staff signed up for CPR classes from CampMinder
              {classData ? ` (${classData.total} staff)` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={classType === "cpr_first_aid" ? "default" : "outline"}
              size="sm"
              onClick={() => setClassType("cpr_first_aid")}
              className={classType === "cpr_first_aid" ? "bg-brand-navy hover:bg-brand-navy/90" : ""}
              data-testid="cpr-class-type-first-aid"
            >
              CPR / First Aid
            </Button>
            <Button
              variant={classType === "cpr_only" ? "default" : "outline"}
              size="sm"
              onClick={() => setClassType("cpr_only")}
              className={classType === "cpr_only" ? "bg-brand-navy hover:bg-brand-navy/90" : ""}
              data-testid="cpr-class-type-cpr-only"
            >
              CPR Only
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={!classData || classData.total === 0}
              data-testid="export-cpr-class-report"
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              Export
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-navy"></div>
          </div>
        ) : !classData || Object.keys(classData.by_date).length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Users className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No staff signed up for {classType === "cpr_only" ? "CPR Only" : "CPR & First Aid"} classes yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(classData.by_date).map(([dateKey, staff]) => (
              <div key={dateKey} className="border border-slate-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleDate(dateKey)}
                  className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                  data-testid={`cpr-date-group-${dateKey.replace(/[^a-zA-Z0-9]/g, '-')}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 text-sm">{dateKey}</span>
                    <span className="text-xs bg-brand-navy/10 text-brand-navy px-2 py-0.5 rounded-full font-medium">
                      {staff.length} staff
                    </span>
                  </div>
                  {expanded[dateKey] ? (
                    <ChevronUp className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  )}
                </button>
                {expanded[dateKey] && (
                  <div className="divide-y divide-slate-100">
                    {staff.map((s, i) => (
                      <div key={i} className="px-4 py-2 flex items-center justify-between text-sm">
                        <span className="font-medium text-slate-900">{s.name}</span>
                        <span className="text-slate-500">{s.position}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const { token } = useAuth();
  const [reportData, setReportData] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [exporting, setExporting] = useState(false);
  const [campStart, setCampStart] = useState("");
  const [campEnd, setCampEnd] = useState("");

  const fetchCampDates = async () => {
    try {
      const resp = await axios.get(`${API}/settings/camp-dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampStart(resp.data.camp_start);
      setCampEnd(resp.data.camp_end);
    } catch {
      // Fallback defaults — don't block render
      setCampStart("2026-06-29");
      setCampEnd("2026-08-20");
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("filter_type", filterType);
      if (filterType === "division" && selectedDivision) {
        params.append("division", selectedDivision);
      }
      
      const response = await axios.get(`${API}/reports?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(response.data);
    } catch {
      toast.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  const fetchDivisions = async () => {
    try {
      const response = await axios.get(`${API}/divisions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDivisions(response.data);
    } catch {
      console.error("Failed to load divisions");
    }
  };

  useEffect(() => {
    fetchDivisions();
    fetchCampDates();
  }, [token]);

  useEffect(() => {
    fetchReport();
  }, [filterType, selectedDivision, token]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append("filter_type", filterType);
      if (filterType === "division" && selectedDivision) {
        params.append("division", selectedDivision);
      }
      
      const response = await axios.get(`${API}/reports/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { csv_data, filename } = response.data;
      const blob = new Blob([csv_data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Report exported successfully");
    } catch {
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-full">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Certification Reports
          </h1>
          <p className="text-slate-500 mt-1">
            {reportData.length} staff member{reportData.length !== 1 ? "s" : ""} in report
          </p>
        </div>
        
        <Button
          onClick={handleExport}
          disabled={exporting || reportData.length === 0}
          className="bg-brand-navy hover:bg-brand-navy/90 btn-lift"
          data-testid="export-report-button"
        >
          <Download className="h-4 w-4 mr-2" />
          {exporting ? "Exporting..." : "Export to CSV"}
        </Button>
      </div>

      {/* Legend: cert status visual language */}
      <div
        className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-700"
        data-testid="cert-status-legend"
      >
        <span className="font-semibold text-slate-900">Status key:</span>
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="h-4 w-4 text-red-600" strokeWidth={2.5} />
          </div>
          <span>Expired — needs renewal now</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="h-4 w-4 text-amber-600" strokeWidth={2.5} />
          </div>
          <span>
            Expires on or before camp ends{campEnd ? ` (${new Date(campEnd).toLocaleDateString()})` : ""} — needs renewal
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center">
            <Check className="h-4 w-4 text-emerald-600" strokeWidth={2.5} />
          </div>
          <span>Covered through end of camp</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-400" strokeWidth={2.5} />
          </div>
          <span>No cert on file</span>
        </div>
      </div>

      {/* CPR Class Roster Panel */}
      <CPRClassPanel token={token} />

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center gap-3">
            <Filter className="h-4 w-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Filter by:</span>
          </div>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48" data-testid="report-filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {filterTypes.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>
                  {filter.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {filterType === "division" && (
            <Select value={selectedDivision} onValueChange={setSelectedDivision}>
              <SelectTrigger className="w-48" data-testid="report-division-filter">
                <SelectValue placeholder="Select division" />
              </SelectTrigger>
              <SelectContent>
                {divisions.map((div) => (
                  <SelectItem key={div} value={div}>
                    {div}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Report Table - Full width with horizontal scroll */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-navy"></div>
          </div>
        ) : reportData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <FileSpreadsheet className="h-6 w-6 text-slate-400" />
            </div>
            <h3 className="font-heading font-semibold text-slate-900 mb-1">
              No data to display
            </h3>
            <p className="text-sm text-slate-500">
              Try adjusting your filters or sync staff from CampMinder
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh] border rounded-md">
            <table className="w-full caption-bottom text-sm min-w-[2200px]">
              <TableHeader className="bg-slate-100 sticky top-0 z-20" style={{boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                <TableRow>
                  <TableHead className="font-semibold whitespace-nowrap sticky left-0 bg-slate-100 z-30 min-w-[180px]">
                    Name
                  </TableHead>
                  <TableHead className="font-semibold whitespace-nowrap min-w-[120px] bg-slate-100">Position</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap min-w-[100px] bg-slate-100">Division</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">CPR/AED</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">CPR Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Combo</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Combo Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">LG Cert</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">LG Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Health</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Health Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Boat</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Boat Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">CEU/CPR</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">CEU Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Epi Pen</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Epi Pen Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">BLS</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">BLS Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">PAL</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">PAL Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">WSI</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">WSI Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">CPR/AED Instr</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">CPR/AED Instr Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Dining</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Dining Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Bus DOT</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Bus DOT Exp</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">Working Papers</TableHead>
                  <TableHead className="font-semibold text-center whitespace-nowrap bg-slate-100">CPR Signup</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap min-w-[160px] bg-slate-100">CPR+FA Class</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap min-w-[160px] bg-slate-100">CPR Only Class</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reportData.map((row, index) => {
                  const prevDivision = index > 0 ? reportData[index - 1].division : null;
                  const showDivisionHeader = row.division !== prevDivision;

                  return (
                    <React.Fragment key={index}>
                      {showDivisionHeader && (
                        <TableRow className="bg-slate-100/80 pointer-events-none">
                          <TableCell colSpan={27} className="py-2 px-4 sticky left-0 bg-slate-100/80 z-10">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                              {row.division || "No Division"}
                            </span>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow
                        className="hover:bg-slate-50/50"
                        data-testid={`report-row-${index}`}
                      >
                    <TableCell className="font-medium whitespace-nowrap sticky left-0 bg-white z-10 text-sm">
                      {row.name}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 whitespace-nowrap">{row.position || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-600 whitespace-nowrap">{row.division || "-"}</TableCell>
                    <TableCell><CertStatusCell hasCert={row.cpr_aed} expirationDate={row.cpr_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.cpr_expiration} hasCert={row.cpr_aed} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><CertStatusCell hasCert={row.cpr_aed_rte_combo} expirationDate={row.combo_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.combo_expiration} hasCert={row.cpr_aed_rte_combo} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><CertStatusCell hasCert={row.lifeguard_cert} expirationDate={row.lg_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.lg_expiration} hasCert={row.lifeguard_cert} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><CertStatusCell hasCert={row.health_cert} expirationDate={row.health_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.health_expiration} hasCert={row.health_cert} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><CertStatusCell hasCert={row.boating_cert} expirationDate={row.boating_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.boating_expiration} hasCert={row.boating_cert} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><CertStatusCell hasCert={row.ceu_cert} expirationDate={row.ceu_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.ceu_expiration} hasCert={row.ceu_cert} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><CertStatusCell hasCert={row.epipen_cert} expirationDate={row.epipen_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.epipen_expiration} hasCert={row.epipen_cert} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><CertStatusCell hasCert={row.bls_cert} expirationDate={row.bls_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.bls_expiration} hasCert={row.bls_cert} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><CertStatusCell hasCert={row.pal_cert} expirationDate={row.pal_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.pal_expiration} hasCert={row.pal_cert} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><CertStatusCell hasCert={row.wsi_cert} expirationDate={row.wsi_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.wsi_expiration} hasCert={row.wsi_cert} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><CertStatusCell hasCert={row.cpr_aed_instructor_cert} expirationDate={row.cpr_aed_instructor_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.cpr_aed_instructor_expiration} hasCert={row.cpr_aed_instructor_cert} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><CertStatusCell hasCert={row.dining_cert} expirationDate={row.dining_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.dining_expiration} hasCert={row.dining_cert} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><CertStatusCell hasCert={row.bus_driver_dot_cert} expirationDate={row.bus_driver_dot_expiration} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell className="text-center"><DateCell value={row.bus_driver_dot_expiration} hasCert={row.bus_driver_dot_cert} campStart={campStart} campEnd={campEnd} /></TableCell>
                    <TableCell><YesNoCell value={row.working_papers} /></TableCell>
                    <TableCell><BooleanCell value={row.signed_up_for_cpr_class} /></TableCell>
                    <TableCell className="text-xs text-slate-700 whitespace-nowrap">
                      {row.cpr_first_aid_class_date || <span className="text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-700 whitespace-nowrap">
                      {row.cpr_only_date || <span className="text-slate-400">-</span>}
                    </TableCell>
                  </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
